import { useState, useEffect, useRef, useMemo } from 'react';
import { API_BASE } from '../config';
import { hasEditAccess } from '../permissionConfig';
import { notifyEditBlocked } from '../components/EditAccessToast';
import { track } from '../trackActivity';
import { exportSheet, num, todayStamp } from '../excel';
import InventoryFileReport from '../components/InventoryFileReport';
import imgKumkumadi from '../assets/sku-banner/kumkumadi.png';
import imgShampoo from '../assets/sku-banner/rosemary-shampoo.png';
import imgSoap from '../assets/sku-banner/sandalwood-soap.png';

// Wait this long after the last keystroke before the search term actually
// triggers a server request — typing "s", "so", "sou" previously fired 3
// separate full round trips (each one an uncached, un-debounced fetch — see
// the old `useEffect(..., [search, statusFilter])`). Found during the
// 2026-10-22 search performance investigation.
const SEARCH_DEBOUNCE_MS = 300;

const emptyForm = {
    ean_number:    '', hsn_number:    '', product_name:  '',
    category:      '', sub_category:  '', listing_date:  '', status: 'Active',
    discontinued_date: '', manufacturer:  '', mfg_location1: '',
    mfg_location2: '', gst_percent:   '', mrp:           '',
    length:        '', width:         '', height:        '', volume: '',
    weight_with_packing: '', pack_size: '', case_pack: '', lead_time: '',
};

// Status chip colours. Statuses come from the data (sku_master.status), so
// anything not listed here falls back to the purple accent.
const STATUS_STYLE = {
    'Active':           { color: '#16A34A', icon: '✅' },
    'Discontinued':     { color: '#DC2626', icon: '⛔' },
    'Festive':          { color: '#D97706', icon: '🪔' },
    'Packing Material': { color: '#64748B', icon: '📦' },
    'Upcoming':         { color: '#2563EB', icon: '🚀' },
};
const statusStyle = (s) => STATUS_STYLE[s] || { color: '#8B5CF6', icon: '🏷️' };
const STATUS_ORDER = ['Active', 'Festive', 'Discontinued', 'Packing Material', 'Upcoming'];

const EXCEL_HEADERS = [
    'EAN Number', 'HSN Number', 'Product Name', 'Category', 'Sub Category',
    'Status', 'Listing Date', 'Discontinued Date',
    'Manufacturer', 'Mfg Location 1', 'Mfg Location 2',
    'GST %', 'MRP',
    'Length (cm)', 'Width (cm)', 'Height (cm)', 'Volume (ml)',
    'Weight With Packing (g)', 'Pack Size', 'Case Pack', 'Lead Time (days)',
];

export default function SKUDescription({ user }) {
    const [skus,          setSkus]          = useState([]);
    const [counts,        setCounts]        = useState({});
    const [byStatus,      setByStatus]      = useState({});
    const [statusOptions, setStatusOptions] = useState([]);
    const [categoryMap,   setCategoryMap]   = useState({}); // category -> [sub categories]
    const [loading,       setLoading]       = useState(false);
    const [submitting,    setSubmitting]    = useState(false);
    const [downloading,   setDownloading]   = useState(false);
    const [msg,           setMsg]           = useState(null);
    const [search,          setSearch]          = useState(''); // what the input shows, every keystroke
    const [debouncedSearch, setDebouncedSearch] = useState(''); // what actually drives the fetch
    const [statusFilter,  setStatusFilter]  = useState([]);     // multi-select; [] = all
    const [showForm,      setShowForm]      = useState(false);
    const [isEdit,        setIsEdit]        = useState(false);
    const [form,          setForm]          = useState(emptyForm);
    const [reportOpen,    setReportOpen]    = useState(false);
    const [unreviewed,    setUnreviewed]    = useState(0);
    const eanRef = useRef();
    // Cancels an in-flight list request when a newer one starts, so a slow
    // response to an earlier keystroke can't land after (and overwrite) a
    // faster response to a later one.
    const abortRef = useRef(null);

    const statusKey = statusFilter.join(',');

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [search]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { loadData(); }, [debouncedSearch, statusKey]);

    // Status counts never depended on search/status (always the full
    // breakdown across every row) — fetched once on mount, and again after
    // an add/edit that could change a SKU's status, instead of being
    // recomputed on every search keystroke for no reason.
    useEffect(() => { loadCounts(); loadUnreviewed(); }, []);

    function buildParams(searchTerm) {
        const params = new URLSearchParams();
        if (searchTerm)          params.set('search', searchTerm);
        if (statusFilter.length) params.set('status', statusFilter.join(','));
        return params;
    }

    async function loadData() {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        try {
            const res  = await fetch(`${API_BASE}/sku-description?${buildParams(debouncedSearch)}`, { signal: controller.signal });
            const data = await res.json();
            setSkus(data.skus || []);
        } catch(e) {
            if (e.name !== 'AbortError') console.error(e);
        }
        finally {
            if (abortRef.current === controller) setLoading(false);
        }
    }

    async function loadCounts() {
        try {
            const res = await fetch(`${API_BASE}/sku-description?action=counts`);
            const data = await res.json();
            setCounts(data.counts || {});
            setByStatus(data.by_status || {});
            setStatusOptions(data.status_options || []);
            setCategoryMap(data.categories || {});
        } catch(e) { console.error(e); }
    }

    // Badge on the Summary Report button — best-effort.
    async function loadUnreviewed() {
        try {
            const res = await fetch(`${API_BASE}/inventory-file-changes?days=30`);
            const data = await res.json();
            setUnreviewed(data.change_counts?.unreviewed || 0);
        } catch { /* badge only */ }
    }

    const chipStatuses = useMemo(() => {
        const all = [...new Set([...statusOptions, ...Object.keys(byStatus)])];
        return all.sort((a, b) => {
            const ia = STATUS_ORDER.indexOf(a), ib = STATUS_ORDER.indexOf(b);
            return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
        });
    }, [statusOptions, byStatus]);

    function toggleStatus(s) {
        setStatusFilter(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
    }

    function handle(key, val) { setForm(f => ({ ...f, [key]: val })); }

    function openNew(prefill = {}) {
        setForm({ ...emptyForm, ...prefill });
        setIsEdit(false);
        setShowForm(true);
        setMsg(null);
        setTimeout(() => eanRef.current?.focus(), 200);
    }

    function openEdit(sku, overrides = {}) {
        setForm({
            ean_number:    sku.ean_number    || '',
            hsn_number:    sku.hsn_number    || '',
            product_name:  sku.product_name  || '',
            category:      (sku.category     || '').trim(),
            sub_category:  (sku.sub_category || '').trim(),
            listing_date:  sku.listing_date  || '',
            status:        sku.status        || 'Active',
            discontinued_date: sku.discontinued_date || '',
            manufacturer:  sku.manufacturer  || '',
            mfg_location1: sku.mfg_location1 || '',
            mfg_location2: sku.mfg_location2 || '',
            gst_percent:   sku.gst_percent   || '',
            mrp:           sku.mrp           || '',
            length:        sku.length        || '',
            width:         sku.width         || '',
            height:        sku.height        || '',
            volume:        sku.volume        || '',
            weight_with_packing: sku.weight_with_packing || '',
            pack_size:     sku.pack_size     || '',
            case_pack:     sku.case_pack     || '',
            lead_time:     sku.lead_time     || '',
            ...overrides,
        });
        setIsEdit(true);
        setShowForm(true);
        setMsg(null);
    }

    // From the Summary Report: open the master record for an EAN (edit), or
    // a pre-filled Add form when the EAN isn't in the master yet.
    async function openFromReport({ ean, name, status, exists }) {
        if (!hasEditAccess(user, 'inventory', 'sku_master')) return notifyEditBlocked();
        if (!exists) { openNew({ ean_number: ean || '', product_name: name || '', status: status && statusOptions.includes(status) ? status : 'Active' }); return; }
        try {
            const res  = await fetch(`${API_BASE}/sku-description?search=${encodeURIComponent(ean)}`);
            const data = await res.json();
            const sku  = (data.skus || []).find(s => s.ean_number === ean);
            if (sku) openEdit(sku);
            else openNew({ ean_number: ean || '', product_name: name || '' });
        } catch { openNew({ ean_number: ean || '', product_name: name || '' }); }
    }

    async function handleSubmit() {
        const required = ['ean_number','product_name','category','status'];
        for (const f of required) {
            if (!form[f]) { setMsg({ type:'error', text:`Required: ${f.replace(/_/g,' ')}` }); return; }
        }
        setSubmitting(true); setMsg(null);
        try {
            const res  = await fetch(`${API_BASE}/sku-description`, {
                method: isEdit ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setMsg({ type:'success', text: data.message });
                track(user?.username, 'EDIT', 'SKU Master', { ean_number: form.ean_number, mode: isEdit ? 'update' : 'create' });
                setShowForm(false);
                setForm(emptyForm);
                await Promise.all([loadData(), loadCounts()]);
            } else {
                setMsg({ type:'error', text: typeof data === 'string' ? data : 'Failed to save' });
            }
        } catch(err) {
            setMsg({ type:'error', text:'Network error: ' + err.message });
        } finally { setSubmitting(false); }
    }

    // Excel of exactly what the current search + status chips select —
    // fetched fresh (not the debounced on-screen list) so a just-typed search
    // is included.
    async function handleDownload() {
        if (downloading) return;
        setDownloading(true);
        try {
            const res  = await fetch(`${API_BASE}/sku-description?${buildParams(search.trim())}`);
            const data = await res.json();
            const rows = data.skus || [];
            if (!rows.length) { alert('No SKUs match these filters'); return; }
            const label = statusFilter.length ? statusFilter.join('_').replace(/\s+/g, '') : 'All';
            exportSheet({
                headers: EXCEL_HEADERS,
                rows: rows.map(r => [
                    r.ean_number, r.hsn_number, r.product_name, r.category, r.sub_category,
                    r.status, r.listing_date, r.discontinued_date,
                    r.manufacturer, r.mfg_location1, r.mfg_location2,
                    num(r.gst_percent), num(r.mrp),
                    num(r.length), num(r.width), num(r.height), num(r.volume),
                    num(r.weight_with_packing), r.pack_size, num(r.case_pack), num(r.lead_time),
                ]),
                sheetName: 'SKU Master',
                filename: `SKU_Master_${label}_${todayStamp()}.xlsx`,
            });
        } catch(err) {
            alert('Download failed: ' + err.message);
        } finally { setDownloading(false); }
    }

    const filtersActive = search || statusFilter.length;

    return (
        <div className="skm">
            <style>{CSS}</style>

            {/* ── Dark hero banner with greyed-out product photography ── */}
            <div className="skm-hero">
                <div className="skm-hero-bg" aria-hidden="true">
                    <img src={imgShampoo}   alt="" className="skm-prod skm-prod-1" />
                    <img src={imgKumkumadi} alt="" className="skm-prod skm-prod-2" />
                    <img src={imgSoap}      alt="" className="skm-prod skm-prod-3" />
                    <div className="skm-hero-fade" />
                    <span className="skm-float skm-float-1">🌿</span>
                    <span className="skm-float skm-float-2">✨</span>
                    <span className="skm-float skm-float-3">🌸</span>
                </div>
                <div className="skm-hero-content">
                    <div>
                        <div className="skm-kicker">Inventory</div>
                        <h1 className="skm-title">SKU <b>Master</b></h1>
                        <p className="skm-sub">Master product catalog — <b>{(counts.total || 0).toLocaleString('en-IN')}</b> SKUs across {chipStatuses.length} statuses.</p>
                        <p className="skm-hint">Click a status below to filter (pick as many as you like), or open the Summary Report to see what changed in the inventory file.</p>
                    </div>
                    <div className="skm-actions">
                        <button className="skm-btn skm-btn-report" onClick={() => setReportOpen(true)}>
                            📊 Summary Report
                            {unreviewed > 0 && <span className="skm-badge">{unreviewed}</span>}
                        </button>
                        <button className="skm-btn skm-btn-dl" onClick={handleDownload} disabled={downloading}>
                            {downloading ? 'Preparing…' : '⬇ Download Excel'}
                        </button>
                        <button className="skm-btn skm-btn-add" onClick={() => { if (!hasEditAccess(user, 'inventory', 'sku_master')) return notifyEditBlocked(); openNew(); }}>
                            + Add New SKU
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Status tiles — multi-select ── */}
            <div className="skm-tiles">
                <button
                    className={`skm-tile${statusFilter.length === 0 ? ' is-on' : ''}`}
                    style={{ '--c': '#8B5CF6' }}
                    onClick={() => setStatusFilter([])}
                >
                    <span className="skm-tile-icon">🗂️</span>
                    <span className="skm-tile-count">{(counts.total || 0).toLocaleString('en-IN')}</span>
                    <span className="skm-tile-label">All SKUs</span>
                    {statusFilter.length === 0 && <span className="skm-tile-check">✓</span>}
                </button>
                {chipStatuses.map(s => {
                    const st = statusStyle(s);
                    const on = statusFilter.includes(s);
                    return (
                        <button key={s} className={`skm-tile${on ? ' is-on' : ''}`} style={{ '--c': st.color }} onClick={() => toggleStatus(s)} aria-pressed={on}>
                            <span className="skm-tile-icon">{st.icon}</span>
                            <span className="skm-tile-count">{(byStatus[s] || 0).toLocaleString('en-IN')}</span>
                            <span className="skm-tile-label">{s}</span>
                            {on && <span className="skm-tile-check">✓</span>}
                        </button>
                    );
                })}
            </div>

            {/* ── Search + active filters ── */}
            <div className="skm-filterbar">
                <div className="skm-search">
                    <span>🔍</span>
                    <input type="text" placeholder="Search SKU name, EAN or category..." value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <div className="skm-active">
                    {statusFilter.length === 0
                        ? <span className="skm-pill skm-pill-muted">All statuses</span>
                        : statusFilter.map(s => (
                            <span key={s} className="skm-pill" style={{ '--c': statusStyle(s).color }}>
                                {s}
                                <button onClick={() => toggleStatus(s)} aria-label={`Remove ${s}`}>×</button>
                            </span>
                        ))}
                </div>
                {filtersActive ? <button className="skm-reset" onClick={() => { setSearch(''); setStatusFilter([]); }}>Reset</button> : null}
                <span className="skm-count">{loading ? 'Loading…' : `${skus.length.toLocaleString('en-IN')} SKUs`}</span>
            </div>

            {/* ── Table ── */}
            <div className="skm-table-wrap" data-tutorial="sku-master-table">
                <table className="skm-table">
                    <thead>
                        <tr>
                            {['Action','EAN','HSN','Product Name','Category','Sub Category','Status',
                              'Loc 1','Loc 2','MRP','L','W','H',
                              'Weight','Pack Size','Case Pack','Lead','GST %'].map(h => <th key={h}>{h}</th>)}
                        </tr>
                    </thead>
                    <tbody style={{ opacity: loading ? 0.5 : 1 }}>
                        {skus.length === 0 ? (
                            <tr><td colSpan={18} className="skm-empty">{loading ? 'Loading…' : 'No SKUs found'}</td></tr>
                        ) : skus.map((s, i) => {
                            const st = statusStyle(s.status);
                            return (
                                <tr key={s.ean_number || i}>
                                    <td>
                                        <button className="skm-edit" title="Edit SKU" onClick={() => { if (!hasEditAccess(user, 'inventory', 'sku_master')) return notifyEditBlocked(); openEdit(s); }}>✏️</button>
                                    </td>
                                    <td className="skm-mono">{s.ean_number}</td>
                                    <td className="skm-hsn">{s.hsn_number || '—'}</td>
                                    <td className="skm-name">{s.product_name}</td>
                                    <td>{s.category || '—'}</td>
                                    <td>{s.sub_category || '—'}</td>
                                    <td><span className="skm-status" style={{ '--c': st.color }}>{s.status}</span></td>
                                    <td>{s.mfg_location1  || '—'}</td>
                                    <td>{s.mfg_location2  || '—'}</td>
                                    <td className="skm-muted">{s.mrp || '—'}</td>
                                    <td className="skm-muted">{s.length    || '—'}</td>
                                    <td className="skm-muted">{s.width     || '—'}</td>
                                    <td className="skm-muted">{s.height    || '—'}</td>
                                    <td className="skm-muted">{s.weight_with_packing || '—'}</td>
                                    <td className="skm-muted">{s.pack_size || '—'}</td>
                                    <td className="skm-muted">{s.case_pack || '—'}</td>
                                    <td className="skm-muted">{s.lead_time || '—'}</td>
                                    <td className="skm-muted">{s.gst_percent || '—'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {reportOpen && (
                <InventoryFileReport
                    user={user}
                    onClose={() => { setReportOpen(false); loadUnreviewed(); }}
                    onOpenSku={openFromReport}
                />
            )}

            {/* ── Add / Edit form modal ── */}
            {showForm && (
                <div className="skm-overlay">
                    <div className="skm-modal">
                        <div className="skm-modal-head">
                            <h2>{isEdit ? '✏️ Edit SKU' : '+ Add New SKU'}</h2>
                            <button className="skm-x" onClick={() => { setShowForm(false); setMsg(null); }}>✕</button>
                        </div>

                        {msg && <div className={`skm-msg ${msg.type === 'success' ? 'ok' : 'err'}`}>{msg.text}</div>}

                        <div className="skm-section-label skm-req">★ Required Information</div>
                        <div className="skm-grid2">
                            <Field label="EAN Number *">
                                <input ref={eanRef} type="text" className="skm-input" placeholder="e.g. 8904134012345"
                                    value={form.ean_number} disabled={isEdit} onChange={e => handle('ean_number', e.target.value)} />
                            </Field>
                            <Field label="HSN Number">
                                <input type="text" className="skm-input" placeholder="e.g. 33059090"
                                    value={form.hsn_number} onChange={e => handle('hsn_number', e.target.value)} />
                            </Field>
                            <Field label="Product Name *">
                                <input type="text" className="skm-input" placeholder="Full product name"
                                    value={form.product_name} onChange={e => handle('product_name', e.target.value)} />
                            </Field>
                            <Field label="Category *">
                                <PickOrType
                                    value={form.category}
                                    options={Object.keys(categoryMap).sort((a, b) => a.localeCompare(b))}
                                    placeholder="Select category"
                                    onChange={v => setForm(f => ({ ...f, category: v, sub_category: (categoryMap[v] || []).includes(f.sub_category) ? f.sub_category : '' }))}
                                />
                            </Field>
                            <Field label="Sub Category">
                                <PickOrType
                                    value={form.sub_category}
                                    options={categoryMap[form.category] || []}
                                    placeholder={form.category ? '— None —' : 'Pick a category first'}
                                    allowBlank
                                    onChange={v => handle('sub_category', v)}
                                />
                            </Field>
                            <Field label="Status *">
                                <select className="skm-input" value={form.status} onChange={e => handle('status', e.target.value)}>
                                    {(statusOptions.includes(form.status) || !form.status ? statusOptions : [...statusOptions, form.status]).map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </Field>
                            <Field label="Listing Date">
                                <input type="date" className="skm-input" value={form.listing_date} onChange={e => handle('listing_date', e.target.value)} />
                            </Field>
                            <Field label="Discontinued Date">
                                <input type="date" className="skm-input" value={form.discontinued_date} onChange={e => handle('discontinued_date', e.target.value)} />
                            </Field>
                            <Field label="Manufacturer">
                                <input type="text" className="skm-input" placeholder="Manufacturer name"
                                    value={form.manufacturer} onChange={e => handle('manufacturer', e.target.value)} />
                            </Field>
                            <Field label="Mfg Location 1">
                                <input type="text" className="skm-input" placeholder="Primary manufacturing location"
                                    value={form.mfg_location1} onChange={e => handle('mfg_location1', e.target.value)} />
                            </Field>
                            <Field label="Mfg Location 2">
                                <input type="text" className="skm-input" placeholder="Secondary location (optional)"
                                    value={form.mfg_location2} onChange={e => handle('mfg_location2', e.target.value)} />
                            </Field>
                        </div>

                        <div className="skm-section-label">Dimensions & Logistics (Optional)</div>
                        <div className="skm-grid3">
                            {[
                                ['length', 'Length (cm)', 'number', '0.00'],
                                ['width', 'Width (cm)', 'number', '0.00'],
                                ['height', 'Height (cm)', 'number', '0.00'],
                                ['volume', 'Volume (ml)', 'number', '0.00'],
                                ['weight_with_packing', 'Weight With Packing (g)', 'number', '0.00'],
                                ['pack_size', 'Pack Size', 'text', 'e.g. 100 ml, 250 g'],
                                ['case_pack', 'Case Pack', 'number', 'Units per case'],
                                ['lead_time', 'Lead Time (days)', 'number', '0'],
                                ['gst_percent', 'GST %', 'number', 'e.g. 5, 12, 18'],
                                ['mrp', 'MRP', 'number', '0.00'],
                            ].map(([key, label, type, ph]) => (
                                <Field key={key} label={label}>
                                    <input type={type} className="skm-input" placeholder={ph} value={form[key]} onChange={e => handle(key, e.target.value)} />
                                </Field>
                            ))}
                        </div>

                        <div className="skm-modal-actions">
                            <button className={`skm-btn ${isEdit ? 'skm-btn-update' : 'skm-btn-save'}`} onClick={handleSubmit} disabled={submitting}>
                                {submitting ? 'Saving...' : isEdit ? '✓ Update SKU' : '✓ Add SKU'}
                            </button>
                            <button className="skm-btn skm-btn-ghost" onClick={() => { setShowForm(false); setMsg(null); }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Dropdown of existing values plus "+ Add new…", which swaps to a text box
// (with a link back to the list) for a value the master doesn't have yet.
const ADD_NEW = '__add_new__';
function PickOrType({ value, options, placeholder, allowBlank, onChange }) {
    const known = !value || options.includes(value);
    const [typing, setTyping] = useState(!known);
    useEffect(() => { if (value && !options.includes(value)) setTyping(true); }, [value, options]);
    if (typing) {
        return (
            <div style={{ display: 'flex', gap: 6 }}>
                <input type="text" className="skm-input" autoFocus placeholder="Type new value" value={value} onChange={e => onChange(e.target.value)} />
                <button type="button" className="skm-btn skm-btn-ghost" style={{ padding: '8px 10px', fontSize: 12 }}
                    onClick={() => { setTyping(false); if (!options.includes(value)) onChange(''); }}>List</button>
            </div>
        );
    }
    return (
        <select className="skm-input" value={value} onChange={e => {
            if (e.target.value === ADD_NEW) { setTyping(true); onChange(''); } else onChange(e.target.value);
        }}>
            <option value="" disabled={!allowBlank}>{placeholder}</option>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
            <option value={ADD_NEW}>+ Add new…</option>
        </select>
    );
}

function Field({ label, children }) {
    return (
        <div className="skm-field">
            <label>{label}</label>
            {children}
        </div>
    );
}

// Colours come from the --acc-* theme variables (Accounts.css), so the page
// follows the Light / Night / Dark switcher in the sidebar. The hero stays
// dark in every theme, like Gifting & Dispatch.
const CSS = `
.skm { font-family: 'Space Grotesk', system-ui, sans-serif; color: var(--acc-text); }
.skm-hero { position: relative; overflow: hidden; border-radius: 20px; padding: 30px 32px; margin-bottom: 18px;
  background: linear-gradient(135deg, #2A2140 0%, #1A1529 55%, #120D1D 100%);
  border: 1px solid #2A2338; box-shadow: 0 10px 30px rgba(20,16,31,0.3); }
.skm-hero-bg { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
.skm-prod { position: absolute; object-fit: cover; border-radius: 18px; filter: grayscale(1) contrast(1.1);
  opacity: 0.16; animation: skm-drift 12s ease-in-out infinite; box-shadow: 0 10px 30px rgba(0,0,0,0.4); }
.skm-prod-1 { width: 170px; height: 220px; right: 4%;  top: -18px; transform: rotate(6deg); animation-duration: 11s; }
.skm-prod-2 { width: 190px; height: 250px; right: 22%; top: 20px;  transform: rotate(-5deg); animation-duration: 13s; animation-delay: 1.2s; }
.skm-prod-3 { width: 230px; height: 165px; right: 40%; top: 58px;  transform: rotate(3deg); animation-duration: 10s; animation-delay: .6s; }
.skm-hero-fade { position: absolute; inset: 0; background: linear-gradient(90deg, #1E1830 30%, rgba(30,24,48,0.55) 62%, rgba(30,24,48,0.15) 100%); }
.skm-float { position: absolute; font-size: 26px; opacity: .18; animation: skm-float 9s ease-in-out infinite; }
.skm-float-1 { left: 3%; top: 70%; } .skm-float-2 { left: 52%; top: 12%; animation-delay: 1s; } .skm-float-3 { left: 90%; top: 72%; animation-delay: 2s; }
@keyframes skm-drift { 0%,100% { translate: 0 0; } 50% { translate: 0 -12px; } }
@keyframes skm-float { 0%,100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-14px) rotate(8deg); } }
.skm-hero-content { position: relative; z-index: 1; display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; flex-wrap: wrap; }
.skm-kicker { font-size: 12px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: #A48FD1; }
.skm-title { font-size: 30px; font-weight: 500; margin: 6px 0 8px; color: #fff; letter-spacing: -0.01em; }
.skm-sub { font-size: 14.5px; color: #B2AAC4; margin: 0; }
.skm-sub b { color: #fff; }
.skm-hint { font-size: 13px; color: #9A90AE; margin: 8px 0 0; font-style: italic; max-width: 520px; }
.skm-actions { display: flex; gap: 10px; flex-wrap: wrap; }
.skm-btn { position: relative; display: inline-flex; align-items: center; gap: 8px; padding: 12px 18px; border-radius: 10px; border: none;
  font: inherit; font-weight: 700; font-size: 13.5px; cursor: pointer; transition: transform .15s ease, box-shadow .15s ease, filter .15s ease; }
.skm-btn:hover:not(:disabled) { transform: translateY(-2px); filter: brightness(1.08); }
.skm-btn:disabled { opacity: .55; cursor: default; }
.skm-btn-report { background: rgba(255,255,255,0.08); color: #fff; border: 1px solid rgba(255,255,255,0.22); }
.skm-btn-report:hover { box-shadow: 0 8px 18px rgba(164,143,209,0.35); }
.skm-btn-dl { background: #5C7E60; color: #fff; }
.skm-btn-dl:hover { box-shadow: 0 8px 18px rgba(92,126,96,0.45); }
.skm-btn-add { background: #6E58A6; color: #fff; }
.skm-btn-add:hover { box-shadow: 0 8px 18px rgba(110,88,166,0.5); }
.skm-badge { position: absolute; top: -8px; right: -8px; min-width: 20px; height: 20px; padding: 0 6px; border-radius: 999px;
  background: #D0202A; color: #fff; font-size: 11px; display: grid; place-items: center; box-shadow: 0 0 0 3px #1A1529; animation: skm-pulse 2s ease-out infinite; }
@keyframes skm-pulse { 0% { box-shadow: 0 0 0 3px #1A1529, 0 0 0 3px rgba(208,32,42,.6); } 100% { box-shadow: 0 0 0 3px #1A1529, 0 0 0 12px rgba(208,32,42,0); } }

.skm-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; margin-bottom: 16px; }
.skm-tile { position: relative; display: flex; flex-direction: column; align-items: flex-start; gap: 2px; padding: 14px 16px 14px 18px;
  border-radius: 14px; cursor: pointer; text-align: left; font: inherit; overflow: hidden;
  background: var(--acc-surface); color: var(--acc-text); border: 1.5px solid var(--acc-border);
  box-shadow: 0 2px 8px rgba(20,16,31,0.06); transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease, background .18s ease; }
.skm-tile::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 4px; background: var(--c); opacity: .85; }
.skm-tile:hover { transform: translateY(-3px); box-shadow: 0 10px 22px rgba(20,16,31,0.12); border-color: var(--c); }
.skm-tile.is-on { border-color: var(--c); background: color-mix(in srgb, var(--c) 12%, var(--acc-surface)); box-shadow: 0 8px 20px color-mix(in srgb, var(--c) 28%, transparent); }
.skm-tile-icon { font-size: 18px; }
.skm-tile-count { font-size: 26px; font-weight: 800; line-height: 1.1; color: var(--c); font-variant-numeric: tabular-nums; }
.skm-tile-label { font-size: 11.5px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--acc-text-muted); }
.skm-tile-check { position: absolute; top: 10px; right: 10px; width: 22px; height: 22px; border-radius: 50%; background: var(--c); color: #fff;
  display: grid; place-items: center; font-size: 12px; font-weight: 800; animation: skm-pop .2s ease-out; }
@keyframes skm-pop { from { transform: scale(.4); opacity: 0; } to { transform: scale(1); opacity: 1; } }

.skm-filterbar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; padding: 12px 14px; margin-bottom: 14px; border-radius: 14px;
  background: var(--acc-surface); border: 1px solid var(--acc-border); }
.skm-search { display: flex; align-items: center; gap: 8px; flex: 1 1 320px; padding: 9px 14px; border-radius: 10px;
  background: var(--acc-page-bg); border: 1.5px solid var(--acc-border); transition: border-color .15s ease; }
.skm-search:focus-within { border-color: var(--acc-primary); }
.skm-search input { flex: 1; border: 0 !important; outline: none; background: transparent !important; box-shadow: none !important; font: inherit; font-size: 14px; color: var(--acc-text); }
.skm-active { display: flex; gap: 6px; flex-wrap: wrap; }
.skm-pill { display: inline-flex; align-items: center; gap: 6px; padding: 5px 6px 5px 11px; border-radius: 999px; font-size: 12px; font-weight: 700;
  color: var(--c); background: color-mix(in srgb, var(--c) 14%, transparent); border: 1px solid color-mix(in srgb, var(--c) 35%, transparent); }
.skm-pill button { border: 0; background: color-mix(in srgb, var(--c) 22%, transparent); color: var(--c); width: 18px; height: 18px; border-radius: 50%; cursor: pointer; font-weight: 800; line-height: 1; }
.skm-pill-muted { --c: var(--acc-text-muted); padding-right: 11px; }
.skm-reset { border: 1px solid var(--acc-border); background: transparent; color: var(--acc-text-muted); padding: 8px 14px; border-radius: 8px; font: inherit; font-weight: 600; cursor: pointer; }
.skm-reset:hover { color: var(--acc-danger); border-color: var(--acc-danger); }
.skm-count { margin-left: auto; font-size: 14px; color: var(--acc-text-muted); font-weight: 600; }

.skm-table-wrap { overflow: auto; max-height: calc(100vh - 380px); border-radius: 14px; border: 1px solid var(--acc-border); background: var(--acc-surface); }
.skm-table { width: 100%; min-width: 1200px; border-collapse: collapse; }
.skm-table th { position: sticky; top: 0; z-index: 1; background: var(--acc-grid-header-bg); color: var(--acc-grid-header-text);
  font-size: 11.5px; text-transform: uppercase; letter-spacing: .05em; padding: 11px 12px; text-align: left; white-space: nowrap; }
.skm-table td { padding: 9px 12px; border-bottom: 1px solid var(--acc-border); color: var(--acc-text); white-space: nowrap; font-size: 14px; }
.skm-table tbody tr { transition: background .12s ease; }
.skm-table tbody tr:nth-child(even) { background: color-mix(in srgb, var(--acc-page-bg) 60%, transparent); }
.skm-table tbody tr:hover { background: var(--acc-primary-light); }
.skm-mono { font-family: ui-monospace, Consolas, monospace; font-size: 14px; }
.skm-hsn { font-size: 11.5px !important; color: var(--acc-danger) !important; }
.skm-name { font-weight: 600; min-width: 220px; white-space: normal !important; line-height: 1.4; }
.skm-muted { color: var(--acc-text-muted) !important; }
.skm-empty { padding: 40px !important; text-align: center; color: var(--acc-text-muted) !important; }
.skm-status { padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 700; white-space: nowrap;
  color: var(--c); background: color-mix(in srgb, var(--c) 14%, transparent); }
.skm-edit { padding: 4px 10px; border-radius: 6px; cursor: pointer; font-size: 12px; border: 1px solid var(--acc-border); background: var(--acc-primary-light); transition: transform .12s ease; }
.skm-edit:hover { transform: scale(1.1); }

.skm-overlay { position: fixed; inset: 0; z-index: 500; background: var(--acc-overlay-bg); backdrop-filter: blur(3px);
  display: flex; align-items: flex-start; justify-content: center; overflow-y: auto; padding: 40px 20px; }
.skm-modal { width: 100%; max-width: 760px; background: var(--acc-surface); color: var(--acc-text); border-radius: 18px; padding: 30px;
  border: 1px solid var(--acc-border); box-shadow: 0 24px 48px rgba(0,0,0,0.3); }
.skm-modal-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.skm-modal-head h2 { font-size: 19px; font-weight: 700; margin: 0; }
.skm-x { background: none; border: none; font-size: 22px; cursor: pointer; color: var(--acc-text-muted); }
.skm-msg { padding: 10px 14px; border-radius: 8px; margin-bottom: 16px; font-size: 13px; font-weight: 600; }
.skm-msg.ok { background: var(--acc-success-bg); color: var(--acc-success); }
.skm-msg.err { background: var(--acc-danger-bg); color: var(--acc-danger); }
.skm-section-label { font-size: 11px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--acc-text-muted); margin: 6px 0 12px; }
.skm-req { color: #00A88C; }
.skm-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 20px; }
.skm-grid3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; margin-bottom: 22px; }
.skm-field { display: flex; flex-direction: column; gap: 6px; }
.skm-field label { font-size: 12px; font-weight: 700; color: var(--acc-text-muted); text-transform: uppercase; letter-spacing: .5px; }
.skm-input { padding: 10px 13px; border: 1.5px solid var(--acc-border); border-radius: 8px; font: inherit; font-size: 15px; width: 100%; box-sizing: border-box;
  color: var(--acc-text); background: var(--acc-page-bg); outline: none; transition: border-color .15s, box-shadow .15s; }
.skm .skm-input:focus { border-color: var(--acc-primary) !important; box-shadow: 0 0 0 3px color-mix(in srgb, var(--acc-primary) 22%, transparent) !important; background: var(--acc-page-bg) !important; }
.skm-input:disabled { opacity: .65; }
.skm-modal-actions { display: flex; gap: 12px; }
.skm-btn-save { background: linear-gradient(135deg,#00C9A7,#009E82); color: #fff; }
.skm-btn-update { background: linear-gradient(135deg,#0EA5E9,#0369A1); color: #fff; }
.skm-btn-ghost { background: transparent; color: var(--acc-text-muted); border: 1.5px solid var(--acc-border); }

@media (prefers-reduced-motion: reduce) { .skm-prod, .skm-float, .skm-badge, .skm-tile-check { animation: none !important; } }
@media (max-width: 760px) {
  .skm-hero { padding: 22px 18px; } .skm-prod { display: none; }
  .skm-grid2, .skm-grid3 { grid-template-columns: 1fr; }
  .skm-count { margin-left: 0; }
}
`;
